import React, { ChangeEvent } from "react";
import { RouteComponentProps, Prompt } from "react-router";
import ReactModal from "react-modal";

import withTitle from "../withTitle";
import { V2 } from "../../Api";
import { formatName, formatDateTimeLocal, formatDateLocalMonth } from "../../Utilities";

import "../../question.css";
import ViewToolbar from "../ViewToolbar";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ConfirmationDialog from "../ConfirmationDialog";
import { DateTime } from "luxon";

interface IMetricsAssessmentUpdateViewState {
    isLoading: boolean | string;
    isSaving: boolean | string;
    isFinalizeDialogOpen: boolean;
    isMonthSelectorDialogOpen: boolean;
    hasChanged: boolean;
    startPreviousMonth: boolean;

    assessment: any;
    changedQuestions: { [id: string]: any };
}

class MetricsAssessmentUpdateView extends React.Component<RouteComponentProps<any>, IMetricsAssessmentUpdateViewState> {
    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        const state: IMetricsAssessmentUpdateViewState = {
            isLoading: true,
            isSaving: false,
            isFinalizeDialogOpen: false,
            isMonthSelectorDialogOpen: false,
            hasChanged: false,
            startPreviousMonth: false,

            assessment: {},
            changedQuestions: {}
        };

        this.state = state;

        this.loadAssessment = this.loadAssessment.bind(this);
        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.saveAssessment = this.saveAssessment.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.saveAndFinalize = this.saveAndFinalize.bind(this);
        this.toggleFinalizeDialog = this.toggleFinalizeDialog.bind(this);
        this.cancelMonthSelection = this.cancelMonthSelection.bind(this);
        this.closeMonthSelectorDialog = this.closeMonthSelectorDialog.bind(this);
    }

    public async componentDidMount() {
        if (this.props.match.params.assessment_id === "new") {
            try {
                const lastMonth = DateTime.local().startOf("month").minus({ month: 1 });

                // Find out if there is an assessment from the previous month
                await V2.get(`metrics/assessments/employee/${this.props.match.params.employee_id}/month/${lastMonth.toISODate()}`);

                // An assessment exists; no need to select
                this.setState({
                    isMonthSelectorDialogOpen: false,
                    startPreviousMonth: false,
                });

                this.startNewAssessment();
            }
            catch (e) {
                if (e.statusCode === 404) {
                    // No assessment last month; user must choose
                    this.setState({
                        isLoading: false,
                        isMonthSelectorDialogOpen: true
                    });
                }
                else {
                    this.setState({
                        isLoading: e.message || "Could not determine assessment month"
                    });
                }
            }
        }
        else {
            this.loadAssessment();
        }
    }

    public componentDidUpdate(prevProps: Readonly<RouteComponentProps<any>>) {
        if (prevProps.match.params.assessment_id !== this.props.match.params.assessment_id) {
            this.loadAssessment();
        }
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="viewer-root">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>;
        }

        if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root">
                <div className="error-text">{this.state.isLoading}</div>
            </div>;
        }

        if (this.state.isMonthSelectorDialogOpen) {
            const currentMonth = DateTime.local().startOf("month");
            const previousMonth = currentMonth.minus({ month: 1 });

            return <div className="viewer-root">
                <ReactModal
                    isOpen={this.state.isMonthSelectorDialogOpen} onRequestClose={this.closeMonthSelectorDialog}
                    overlayClassName="modal-overlay" className="modal-content modal-content-500" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open"
                    contentLabel="Choose month">
                    <div className="input-form">
                        <section className="info-block">
                            <header>
                                <h3>Choose Assessment Month</h3>
                            </header>

                            <p>
                                Please select which month this assessment is for.<br />
                                Or you may push Cancel to go back.
                            </p>

                            <div className="action-panel-dialog">
                                <button type="button" className="button" onClick={() => this.selectMonth(true)}>{formatDateLocalMonth(previousMonth)}</button>
                                <button type="button" className="button" onClick={() => this.selectMonth(false)}>{formatDateLocalMonth(currentMonth)}</button>
                                <button type="button" className="button push-right" onClick={this.cancelMonthSelection}>Cancel</button>
                            </div>
                        </section>
                    </div>
                </ReactModal>
            </div>;
        }

        return (
            <div className="viewer-root viewer-narrow info-container question-container">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem>
                            <button type="button" className="text-with-icon" onClick={this.toggleFinalizeDialog}><i className="icon-check-circle-o" /><span>Save & Finalize</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <ConfirmationDialog isOpen={this.state.isFinalizeDialogOpen} description={null} onConfirm={this.saveAndFinalize} onRequestClose={this.toggleFinalizeDialog}>
                    <div>
                        Are you sure you want to finalize this assessment?<br />
                        <span>After you finalize, you <b>cannot edit</b> this assessment further.</span>
                    </div>
                </ConfirmationDialog>
                <header className="info-header">
                    <h2>{formatName(this.state.assessment.employee.name)} &mdash; {formatDateLocalMonth(this.state.assessment.date)}</h2>
                    <small className="small-text">Last updated {formatDateTimeLocal(this.state.assessment.lastUpdated)}</small>
                </header>
                {this.state.assessment.answers.map((a: any) => <section key={a.id} className={"info-block" + ((a.answerText || a.answerNumber || a.answerNumber === 0) ? " answered" : "")}>
                    <span className="question-text">{a.questionText}</span>
                    <span className="question-answer">{this.renderQuestionType(a)}</span>
                    {(a.answerText || a.answerNumber || a.answerNumber === 0) && !(a.questionType.startsWith("text."))
                        ? <span className="question-controls">
                            <button type="button" className="link" onClick={() => this.clearAnswer(a)}>Clear</button>
                        </span>
                        : null}
                </section>)}

                {this.state.isSaving === true
                    ? <div className="spinner lds-dual-ring" />
                    : typeof this.state.isSaving === "string"
                        ? <div className="error-text">{this.state.isSaving}</div>
                        : null}

                <div className="action-panel-dialog">
                    <span className="spring" />
                    <button type="submit" className="button" onClick={this.saveAssessment} disabled={this.state.isSaving === true}>Save</button>
                    <button type="button" className="button" onClick={this.cancelChanges} disabled={this.state.isSaving === true}>Cancel</button>
                </div>
            </div>
        );
    }

    private saveAssessment() {
        this.saveAssessmentWithOptions();
    }

    private async saveAssessmentWithOptions(skipNav = false) {
        try {
            this.setState({
                isSaving: true
            });

            const { changedQuestions, assessment } = this.state;
            const data = {
                answers: [] as any[]
            };

            for (const answer of assessment.answers) {
                if (!changedQuestions[answer.id]) {
                    continue;
                }
                if (changedQuestions[answer.id].originalValue === (answer.answerText || answer.answerNumber)) {
                    delete changedQuestions[answer.id];
                    continue;
                }

                data.answers.push({
                    id: answer.id,
                    questionId: answer.questionId,
                    answerText: answer.answerText,
                    answerNumber: answer.answerNumber
                });
            }

            await V2.patch(`metrics/assessments/${assessment.id}/answers`, data);

            this.setState({
                isSaving: false,
                hasChanged: false,
            }, () => !skipNav && this.props.history.goBack());

            return true;
        }
        catch (e) {
            this.setState({
                isSaving: e.message || "Failed to save"
            });
            return false;
        }
    }

    private cancelChanges() {
        this.props.history.goBack();
    }

    private async startNewAssessment() {
        try {
            const data = {
                employeeId: this.props.match.params.employee_id,
                isPreviousMonth: this.state.startPreviousMonth
            };

            const assessmentId = (await V2.post<any>(`metrics/assessments`, data)).assessmentId;

            this.props.history.replace(`/metrics/assessment/employee/${data.employeeId}/${assessmentId}`);
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not create assessment"
            });
        }
    }

    private async loadAssessment() {
        try {
            const assessment = await V2.get<any>(`metrics/assessments/${this.props.match.params.assessment_id}`);

            this.setState({
                isLoading: false,
                assessment
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load assessment"
            });
        }
    }

    private handleInputChanged(e: ChangeEvent<HTMLInputElement>) {
        const { name, value, checked, type } = e.currentTarget;
        const trueValue = type === "checkbox" ? checked : value;

        this.setState({
            [name]: trueValue
        } as any);
    }

    private handleQuestionChanged(id: string, value: string) {
        const { assessment, changedQuestions } = this.state;

        const answer = assessment.answers.find((a: any) => a.id === id);
        if (!answer) {
            return
        }

        if (!changedQuestions[answer.id]) {
            changedQuestions[answer.id] = { originalValue: answer.answerText || answer.answerNumber };
        }

        if (this.questionIsText(answer.questionType)) {
            answer.answerText = value;
        }
        else {
            answer.answerNumber = +value;
        }

        this.setState({
            assessment,
            changedQuestions,
            hasChanged: true
        });
    }

    private renderQuestionType(a: any) {
        const { questionType, id } = a;
        const answer = (a.answerText || a.answerNumber?.toString()) || "";

        switch (questionType) {
            case "text": return <>
                <input type="text" placeholder="Enter text..." value={answer} onChange={e => this.handleQuestionChanged(id, e.currentTarget.value)} />
            </>;
            case "text.multiline": return <>
                <textarea value={answer} onChange={e => this.handleQuestionChanged(id, e.currentTarget.value)} />
            </>;
            case "yesno": return <>
                <ul className="question-option-list-vertical">
                    {this.renderQuestionRadio(id, answer, "1", "Yes")}
                    {this.renderQuestionRadio(id, answer, "0", "No")}
                </ul>
            </>;
            case "yesno.na": return <>
                <ul className="question-option-list-vertical">
                    {this.renderQuestionRadio(id, answer, "1", "Yes")}
                    {this.renderQuestionRadio(id, answer, "0", "No")}
                    {this.renderQuestionRadio(id, answer, "-1", "N/A")}
                </ul>
            </>;
            case "scale.1-5.generic": return <>
                <ul className="question-option-list">
                    {this.renderQuestionRadio(id, answer, "1")}
                    {this.renderQuestionRadio(id, answer, "2")}
                    {this.renderQuestionRadio(id, answer, "3")}
                    {this.renderQuestionRadio(id, answer, "4")}
                    {this.renderQuestionRadio(id, answer, "5")}
                </ul>
            </>;
            case "scale.m2-2.generic": return <>
                <ul className="question-option-list">
                    {this.renderQuestionRadio(id, answer, "-2")}
                    {this.renderQuestionRadio(id, answer, "-1")}
                    {this.renderQuestionRadio(id, answer, "0")}
                    {this.renderQuestionRadio(id, answer, "+1")}
                    {this.renderQuestionRadio(id, answer, "+2")}
                </ul>
            </>;
            case "scale.m2-2.change": return <>
                <ul className="question-option-list">
                    {this.renderQuestionRadio(id, answer, "-2", "Greatly Worsened")}
                    {this.renderQuestionRadio(id, answer, "-1", "Worsened")}
                    {this.renderQuestionRadio(id, answer, "0", "No Change")}
                    {this.renderQuestionRadio(id, answer, "1", "Improved")}
                    {this.renderQuestionRadio(id, answer, "2", "Greatly Improved")}
                </ul>
            </>;
            case "scale.1-5.expectation": return <>
                <ul className="question-option-list">
                    {this.renderQuestionRadio(id, answer, "1", "Unacceptable")}
                    {this.renderQuestionRadio(id, answer, "2", "Needs Improvement")}
                    {this.renderQuestionRadio(id, answer, "3", "Meets Expectations")}
                    {this.renderQuestionRadio(id, answer, "4", "Exceeds Expectations")}
                    {this.renderQuestionRadio(id, answer, "5", "Outstanding")}
                </ul>
            </>;
            case "scale.1-5.expectation2": return <>
                <ul className="question-option-list">
                    {this.renderQuestionRadio(id, answer, "1", "We Need to Talk")}
                    {this.renderQuestionRadio(id, answer, "2", "Needs Improvement")}
                    {this.renderQuestionRadio(id, answer, "3", "Meets Expectations")}
                    {this.renderQuestionRadio(id, answer, "4", "Exceeds Expectations")}
                    {this.renderQuestionRadio(id, answer, "5", "Outstanding")}
                </ul>
            </>;
            case "scale.1-5.expectation2.na": return <>
                <ul className="question-option-list">
                    {this.renderQuestionRadio(id, answer, "-1", "N/A")}
                    {this.renderQuestionRadio(id, answer, "1", "We Need to Talk")}
                    {this.renderQuestionRadio(id, answer, "2", "Needs Improvement")}
                    {this.renderQuestionRadio(id, answer, "3", "Meets Expectations")}
                    {this.renderQuestionRadio(id, answer, "4", "Exceeds Expectations")}
                    {this.renderQuestionRadio(id, answer, "5", "Outstanding")}
                </ul>
            </>;
            case "scale.1-5.agree": return <>
                <ul className="question-option-list">
                    {this.renderQuestionRadio(id, answer, "1", "Strongly Disagree")}
                    {this.renderQuestionRadio(id, answer, "2", "Disagree")}
                    {this.renderQuestionRadio(id, answer, "3", "Neutral")}
                    {this.renderQuestionRadio(id, answer, "4", "Agree")}
                    {this.renderQuestionRadio(id, answer, "5", "Strongly Agree")}
                </ul>
            </>;
            default: return <span className="error-box slim inline">Unknown question type <b>{questionType}</b></span>;
        }
    }

    private questionIsText(type: string) {
        if (type.startsWith("scale.") || type.startsWith("yesno"))
            return false;
        else
            return true;
    }

    private renderQuestionRadio(id: string, answer: string, value: string, label?: string) {
        return <li className="question-checkbox">
            <input type="radio" name={`q_${id}`} id={`q_${id}_${value}`} value={value} checked={answer === value} onChange={e => this.handleQuestionChanged(id, e.currentTarget.value)} />
            <label htmlFor={`q_${id}_${value}`} tabIndex={0}>
                {label || value}
                <span />
            </label>
        </li>;
    }

    private async saveAndFinalize() {
        if (!await this.saveAssessmentWithOptions(true))
            return;

        await V2.post(`metrics/assessments/${this.state.assessment.id}/finalize`);

        this.props.history.goBack();
    }

    private toggleFinalizeDialog() {
        const isOpen = this.state.isFinalizeDialogOpen;

        this.setState({
            isFinalizeDialogOpen: !isOpen
        });
    }

    private clearAnswer(answer: any) {
        const { assessment, changedQuestions } = this.state;

        if (!changedQuestions[answer.id]) {
            changedQuestions[answer.id] = { originalValue: answer.answerText || answer.answerNumber };
        }

        answer.answerNumber = undefined;
        answer.answerText = undefined;

        this.setState({
            hasChanged: true,
            assessment
        });
    }

    private closeMonthSelectorDialog() {
        this.setState({
            isMonthSelectorDialogOpen: false
        });
    }

    private cancelMonthSelection() {
        this.props.history.goBack();
    }

    private selectMonth(isPrevious: boolean) {
        this.setState({
            startPreviousMonth: isPrevious,
            isMonthSelectorDialogOpen: false,
            isLoading: true,
        }, () => this.startNewAssessment());
    }
}

export default withTitle("Employee Assessment", MetricsAssessmentUpdateView);
